import { XIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";

import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { formatTimestamp } from "@/utils/timestamp";

const OutputHeader = ({
  provider,
  modelName,
  outputNumber,
  isCollapsed,
  onToggleCollapse,
  createdAt,
  onDelete,
}: {
  provider: string;
  modelName: {
    model_name?: string;
    model_config_display_name?: string | null;
  };
  outputNumber?: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  createdAt?: string;
  onDelete?: () => void;
}) => {
  const { model_name, model_config_display_name } = modelName;

  return (
    <div
      onClick={onToggleCollapse}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggleCollapse();
        }
      }}
      className="flex cursor-pointer select-none items-center justify-between border-b border-gray-100/80 bg-white/90 px-3 py-2.5 transition-colors duration-200 hover:bg-gray-50/90"
      role="button"
      tabIndex={0}
      aria-label={isCollapsed ? "Expand content" : "Collapse content"}
    >
      <div className="flex min-w-0 items-center gap-2.5 overflow-hidden">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-400">
          <svg
            className={`h-4 w-4 transform transition-transform duration-200 ${
              isCollapsed ? "-rotate-30" : "rotate-90"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex-shrink-0 text-sm font-semibold text-gray-700 transition-colors hover:text-gray-900">
            {provider || "Output"}
          </span>
          {outputNumber !== undefined && (
            <span className="flex-shrink-0 font-mono text-[11px] font-medium tabular-nums text-gray-400">
              #{outputNumber}
            </span>
          )}
        </div>
        <span className="mr-4 flex h-7 min-w-0 max-w-[220px] flex-shrink items-center justify-start gap-1.5 rounded-full px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100">
          <ProviderWithLogo hideName provider={provider || ""} className="!mr-0" />
          <span className="truncate">{model_config_display_name || model_name}</span>
        </span>
      </div>
      <div className="flex flex-shrink-0 items-center gap-3">
        {createdAt && <span className="text-xs text-gray-400">{formatTimestamp(createdAt)}</span>}
        {onDelete && (
          <BasicTooltip content="Delete output">
            <Button
              variant="ghostGray"
              size="tinyIcon"
              onClick={event => {
                event.stopPropagation();
                onDelete();
              }}
            >
              <XIcon className="h-4 w-auto text-gray-400" />
            </Button>
          </BasicTooltip>
        )}
      </div>
    </div>
  );
};

export default observer(OutputHeader);
